import React, { useEffect, useState } from 'react'
import { Button, Cell, Checkbox, Divider, Grid, Select, TextField } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { DmcDatePicker } from '../../../common/DmcDatePicker/DmcDatePicker'
import DmcTemplePicker from '../../../common/DmcTemplePicker'

export default function OdmiRecordForm({ handleSubmit, language, location, history }) {
  const searchParams = new URLSearchParams(location.search)
  const { t } = useTranslation()
  const initialState = {
    recordNbr: '',
    tpin: '',
    surname: '',
    givenName: '',
    matchCase: false,
    eventType: '*',
    year: '',
    month: '',
    day: '',
    bc: false,
    transmission: '',
    ordTemple: '',
    entryNbr: '',
    batchNbr: '',
    submitterName: '',
    ors: true,
    fns: true,
    ordCard: true,
    unisys: true,
  }
  const [query, setQuery] = useState({
    recordNbr: searchParams.get('recordNbr') || '',
    tpin: searchParams.get('tpin') || '',
    surname: searchParams.get('surname') || '',
    givenName: searchParams.get('givenName') || '',
    matchCase: searchParams.get('matchCase') || false,
    eventType: searchParams.get('eventType') || '*',
    year: searchParams.get('year') || '',
    month: searchParams.get('month') || '',
    day: searchParams.get('day') || '',
    bc: searchParams.get('bc') || false,
    transmission: searchParams.get('transmission') || '',
    ordTemple: searchParams.get('ordTemple') || '',
    entryNbr: searchParams.get('entryNbr') || '',
    batchNbr: searchParams.get('batchNbr') || '',
    submitterName: searchParams.get('submitterName') || '',
    ors: searchParams.get('ors') || true,
    fns: searchParams.get('fns') || true,
    ordCard: searchParams.get('ordCard') || true,
    unisys: searchParams.get('unisys') || true,
  })
  const [eventDate, setEventDate] = useState({
    key: 'date',
    primaryText: `${query.day} ${query.month} ${query.year}`.trim(),
  })

  const isEqual = (obj1, obj2) => {
    return (
      obj1.recordNbr === obj2.recordNbr &&
      obj1.tpin === obj2.tpin &&
      obj1.surname === obj2.surname &&
      obj1.givenName === obj2.givenName &&
      obj1.matchCase === obj2.matchCase &&
      obj1.eventType === obj2.eventType &&
      obj1.year === obj2.year &&
      obj1.month === obj2.month &&
      obj1.day === obj2.day &&
      obj1.bc === obj2.bc &&
      obj1.transmission === obj2.transmission &&
      obj1.ordTemple === obj2.ordTemple &&
      obj1.entryNbr === obj2.entryNbr &&
      obj1.batchNbr === obj2.batchNbr &&
      obj1.submitterName === obj2.submitterName &&
      obj1.ors === obj2.ors &&
      obj1.fns === obj2.fns &&
      obj1.ordCard === obj2.ordCard &&
      obj1.unisys === obj2.unisys
    )
  }

  useEffect(() => {
    if (!searchParams.get('search')?.startsWith('odm')) {
      if (!isEqual(query, initialState)) {
        handleSubmit(query)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const setDate = (value) => {
    if (value) {
      setEventDate(value)
      const parts = value.formal?.replace(/^\+/, '')?.split('-')
      if (parts?.length === 3) {
        setQuery({ ...query, year: parts[0], month: parts[1], day: parts[2] })
      } else if (parts?.length === 2) {
        setQuery({ ...query, year: parts[0], month: parts[1] })
      } else if (parts) {
        setQuery({ ...query, year: parts[0] })
      }
    } else {
      eventDate.primaryText = ''
      setEventDate(eventDate)
      setQuery({ ...query, year: '', month: '', day: '' })
    }
  }

  const submit = (event) => {
    event.preventDefault()
    let queryString = `search=${language}`

    Object.keys(query).forEach((queryKey) => {
      if (query[queryKey] !== initialState[queryKey]) {
        queryString += `&${queryKey}=${query[queryKey]}`
      }
    })

    if (queryString !== `search=${language}`) {
      history.push(`/search/odv?${queryString}`)
    }
  }

  const reset = () => {
    setQuery(initialState)
    setEventDate({
      key: 'date',
      primaryText: '',
    })
    handleSubmit(null)
  }

  return (
    <form onSubmit={(event) => submit(event)}>
      <Divider label={t('odmi.form.individual', 'Individual')} />
      <Grid>
        <Cell columns={5}>
          <TextField
            label={t('familyName', 'Family Name')}
            value={query.surname}
            onChange={(event) => setQuery({ ...query, surname: event.target.value })}
          />
        </Cell>
        <Cell columns={5}>
          <TextField
            label={t('givenName', 'Given Name')}
            value={query.givenName}
            onChange={(event) => setQuery({ ...query, givenName: event.target.value })}
          />
        </Cell>
        <Cell columns={2} alignY="middle">
          <Checkbox
            name="case"
            label={t('matchCase', 'Match Case')}
            checked={query.matchCase}
            onChange={() => setQuery({ ...query, matchCase: !query.matchCase })}
          />
        </Cell>
        <Cell columns={6}>
          <TextField
            label={t('recordNumber', 'Record Number')}
            value={query.recordNbr}
            onChange={(event) => setQuery({ ...query, recordNbr: event.target.value })}
          />
        </Cell>
        <Cell columns={6}>
          <TextField
            label={t('tpin', 'TPIN')}
            value={query.tpin}
            onChange={(event) => setQuery({ ...query, tpin: event.target.value })}
          />
        </Cell>
        <Divider label={t('event', 'Event')} />
        <Cell columns={5}>
          <Select
            id="odmiRecordEventType"
            label={t('odm.event.type', 'Type')}
            value={query.eventType}
            onChange={(event) => setQuery({ ...query, eventType: event.target.value })}
            options={[
              { value: 'Adult Christening', label: t('odmi.event.adultChristening', 'Adult Christening') },
              { value: 'Birth', label: t('odmi.event.birth', 'Birth') },
              { value: 'Burial', label: t('odmi.event.burial', 'Burial') },
              { value: 'Census', label: t('odmi.event.census', 'Census') },
              { value: 'Christening', label: t('odmi.event.christening', 'Christening') },
              { value: 'Death', label: t('odmi.event.death', 'Death') },
              { value: 'Marriage', label: t('odmi.event.marriage', 'Marriage') },
              { value: 'Other', label: t('odmi.event.other', 'Other') },
              { value: 'Probate', label: t('odmi.event.probate', 'Probate') },
              { value: 'Will', label: t('odmi.event.will', 'Will') },
            ]}
          />
        </Cell>
        <Cell columns={5}>
          <DmcDatePicker
            selectedItem={eventDate}
            setSelectedItem={setDate}
            label={t('date', 'Date')}
            dayMonthYearDateFormat="yyyyMMDD"
            monthYearDateFormat="yyyy"
            yearDateFormat="yyyy"
          />
        </Cell>
        <Cell columns={2} alignY="middle">
          <Checkbox
            name="bc"
            label={t('bc', 'BC')}
            checked={query.bc}
            onChange={() => setQuery({ ...query, bc: !query.bc })}
          />
        </Cell>
        <Divider label={t('source', 'Source')} />
        <Cell columns={3}>
          <TextField
            label={t('odmi.transmission', 'Transmission')}
            value={query.transmission}
            onChange={(event) => setQuery({ ...query, transmission: event.target.value })}
          />
        </Cell>
        <Cell columns={3}>
          <DmcTemplePicker
            onSuggestionSelected={(selection) => selection && setQuery({ ...query, ordTemple: selection.templeCode })}
          />
        </Cell>
        <Cell columns={3}>
          <TextField
            label={t('odmi.entryNumber', 'Entry Number')}
            value={query.entryNbr}
            onChange={(event) => setQuery({ ...query, entryNbr: event.target.value })}
          />
        </Cell>
        <Cell columns={3}>
          <TextField
            label={t('odmi.batchNumber', 'Batch Number')}
            value={query.batchNbr}
            onChange={(event) => setQuery({ ...query, batchNbr: event.target.value })}
          />
        </Cell>
        <Cell columns={6}>
          <TextField
            label={t('submitter', 'Submitter')}
            value={query.submitterName}
            onChange={(event) => setQuery({ ...query, submitterName: event.target.value })}
          />
        </Cell>
        <Cell columns={2} alignY="middle">
          <Checkbox
            name="ors"
            label={t('odmi.ors', 'ORS')}
            checked={query.ors}
            onChange={() => setQuery({ ...query, ors: !query.ors })}
          />
        </Cell>
        <Cell columns={2} alignY="middle">
          <Checkbox
            name="fns"
            label={t('odmi.fns', 'FNS / TNS')}
            checked={query.fns}
            onChange={() => setQuery({ ...query, fns: !query.fns })}
          />
        </Cell>
        <Cell columns={2} alignY="middle">
          <Checkbox
            name="ordCard"
            label={t('odmi.ordCard', 'Ordinance Cards')}
            checked={query.ordCard}
            onChange={() => setQuery({ ...query, ordCard: !query.ordCard })}
          />
        </Cell>
        <Cell columns={1}>
          <Button id="odvSearchBtn" type="submit" emphasis="high">
            {t('action.search', 'Search')}
          </Button>
        </Cell>
        <Cell columns={11}>
          <Button emphasis="medium" onClick={reset}>
            {t('action.clear', 'Clear')}
          </Button>
        </Cell>
      </Grid>
    </form>
  )
}
